import React, { useEffect, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import '../pages/style/ProductCarrousel.css';
import { ProductCarouselProps } from '../../@types/Product';
import { Box, Pagination } from '@mui/material';
import ProductCard from './ProductCard';
import { useSearch } from '../../contexts/SearchContext';


const ProductCarousel: React.FC<ProductCarouselProps> = ({ products, columns = 4, rows = 2 }) => {
  const [responsiveColumns, setResponsiveColumns] = useState(columns);
  const [currentPage, setCurrentPage] = useState(1); // Página actual, comenzando desde 1
  const { searchTerm } = useSearch(); // Obtener el término de búsqueda desde el contexto

  // Calcular cuántos productos mostrar por slide (filas x columnas)
  const itemsPerSlide = rows * responsiveColumns;

  // Filtrar productos según el término de búsqueda
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const slides = [];

  // Agrupar productos filtrados en slides
  for (let i = 0; i < filteredProducts.length; i += itemsPerSlide) {
    slides.push(filteredProducts.slice(i, i + itemsPerSlide));
  }

  // Hook para manejar la cantidad de columnas según el ancho de la pantalla
  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width < 600) {
        setResponsiveColumns(2); // En pantallas pequeñas 2 columnas
      } else if (width < 900) {
        setResponsiveColumns(3); // En pantallas medianas 3 columnas
      } else {
        setResponsiveColumns(columns); // Por defecto, el número de columnas que se pasa
      }
    };

    // Actualizar columnas cuando el componente se monta o el tamaño de la ventana cambia
    updateColumns();
    window.addEventListener('resize', updateColumns);

    return () => window.removeEventListener('resize', updateColumns);
  }, [columns]);

  // Hook para resetear la página actual a 1 cuando cambie el término de búsqueda
  useEffect(() => {
    setCurrentPage(1); // Resetear a la primera página
  }, [searchTerm]);

  // Función para cambiar la página
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page); // Actualizamos a la página seleccionada
  };

  // Variables para manejo táctil
  let touchStartY = 0;
  let touchStartX = 0;
  let touchEndY = 0;
  let touchEndX = 0;

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    touchEndX = e.touches[0].clientX;
    touchEndY = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    const deltaX = touchEndX - touchStartX;
    const deltaY = touchEndY - touchStartY;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Swipe horizontal
      if (deltaX > 50) {
        setCurrentPage((prevPage) => Math.max(prevPage - 1, 1)); // Desplazamiento izquierda
      } else if (deltaX < -50) {
        setCurrentPage((prevPage) => Math.min(prevPage + 1, slides.length)); // Desplazamiento derecha
      }
    } else {
      // Permitir desplazamiento vertical normal
      window.scrollBy(0, -deltaY);
    }
  };

  return (
    <div
      className="carousel-container"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <h2 className="carousel-title">Nuestros Productos</h2>
      <Carousel
        selectedItem={currentPage - 1} // Usar el estado de la página actual, ajustando a base 0
        onChange={(index: number) => setCurrentPage(index + 1)} // Cambiar la página cuando se hace un cambio de slide
        showArrows={false} // Deshabilitar las flechas de navegación
        showThumbs={false}
        infiniteLoop={false}
        showStatus={false}
        showIndicators={false}
        swipeable={false} // Desactivar swipe nativo, manejamos manualmente
        dynamicHeight={false}
      >
        {slides.map((slide, index) => (
          <div className="slide" key={index}>
            <div
              className="product-grid"
              style={{
                gridTemplateColumns: `repeat(${responsiveColumns}, 1fr)`,
                gridTemplateRows: `repeat(${rows}, auto)`,
                gap: '16px',
              }}
            >
              {slide.map((product, idx) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        ))}
      </Carousel>

      {/* Paginación debajo del carrusel */}
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
        <Pagination
          count={slides.length}
          page={currentPage} // Mostrar la página base 1 para el usuario
          onChange={handlePageChange}
          color="primary"
          size="large"
          shape="rounded"
          siblingCount={1}
          boundaryCount={1}
          sx={{
            '& .MuiPaginationItem-root': {
              fontSize: '16px',
              fontWeight: 'bold',
              borderRadius: '8px',
            },
          }}
        />
      </Box>
    </div>
  );
};

export default ProductCarousel;
